import { Component, computed, effect, input, model, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentNodeModel } from '../../core/models/data/document.model';
import { DocumentPermission } from '../../core/enums/data.enums';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { DocumentUtilities } from '../../core/utilities/document.utilities';
import { DocumentsMessagingConstants } from '../../core/constants/messaging.constants';

/** Sentinel for selecting the library root (top level) as a destination. */
export enum FolderTreeDestination {
    Root = 'root'
}

/** A picked destination: an existing folder id, the library root, or nothing yet. */
export type FolderTreeSelection = number | FolderTreeDestination | undefined;

/** Fetches a folder's immediate child folders. Supplied by the host so the part stays presentational. */
export type LoadFolderChildren = (folderId: number) => Promise<DocumentNodeModel[]>;

/**
 * Reusable lazy collapsible folder-tree destination picker. Seeds from the org's top-level folders
 * and fetches a folder's children only when it is first expanded (via `loadChildren`) — no upfront
 * crawl of the whole repository. A node shows an expand chevron when its `childFolderCount > 0`, so
 * expandability is known without fetching. Tracks a single selected destination (two-way via
 * `selected`). Hosts configure which nodes are omitted (`excludedIds`), shown-but-inert
 * (`disabledIds`/`rootDisabled`), and whether the root is offered.
 */
@Component({
    selector: 'ms-folder-tree-picker',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, TooltipDirective],
    templateUrl: './folder-tree-picker.component.html',
    styleUrl: './folder-tree-picker.component.scss'
})
export class FolderTreePickerComponent {

    // The org's top-level folders — the seed level of the tree. Deeper levels load on demand.
    public rootNodes = input<DocumentNodeModel[]>([]);
    // Fetches a folder's immediate child folders when it is first expanded.
    public loadChildren = input<LoadFolderChildren | undefined>(undefined);
    // Two-way selected destination — a folder id, the Root sentinel, or undefined.
    public selected = model<FolderTreeSelection>(undefined);

    // Folders omitted from the tree entirely (e.g. a move source — hiding it makes its whole subtree
    // unreachable, which is exactly the "can't move into self/descendant" rule).
    public excludedIds = input<Set<number>>(new Set<number>());
    // Folders shown but not selectable (e.g. the source's current location), annotated with disabledNote.
    public disabledIds = input<Set<number>>(new Set<number>());
    public disabledNote = input<string>('');

    // Whether to offer the library root as a destination, and whether it's currently selectable.
    public showRoot = input<boolean>(false);
    public rootDisabled = input<boolean>(false);
    public rootPermission = input<DocumentPermission>(DocumentPermission.AdminOnly);
    public rootLabel = input<string>(DocumentsMessagingConstants.DOCUMENTS_ROOT_LABEL());

    // Message shown when there are no selectable root folders (copy varies by host modal).
    public emptyMessage = input<string>('');

    public expanded = signal<Set<number>>(new Set<number>());
    // Children fetched per folder id (sorted on store), and which folders are currently fetching.
    private loadedChildren = signal<Map<number, DocumentNodeModel[]>>(new Map<number, DocumentNodeModel[]>());
    private loadingIds = signal<Set<number>>(new Set<number>());

    public DocumentPermission = DocumentPermission;
    public DocumentUtilities = DocumentUtilities;
    public FolderTreeDestination = FolderTreeDestination;

    public rootFolders = computed<DocumentNodeModel[]>(() => {
        const excluded = this.excludedIds();
        return this.rootNodes()
            .filter(n => !excluded.has(n.documentNodeId))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    });

    constructor() {
        // Reset the tree when the host clears the selection (e.g. on modal reopen) so a new context
        // (different source folder / exclusions) starts collapsed with a fresh cache.
        effect(() => {
            if (this.selected() === undefined) {
                this.expanded.set(new Set<number>());
                this.loadedChildren.set(new Map<number, DocumentNodeModel[]>());
                this.loadingIds.set(new Set<number>());
            }
        });
    }

    // A folder is expandable when it reports child folders — known from the node itself, no fetch needed.
    public canExpand(node: DocumentNodeModel): boolean {
        return (node.childFolderCount ?? 0) > 0;
    }

    public getChildren(folderId: number): DocumentNodeModel[] {
        const kids = this.loadedChildren().get(folderId) ?? [];
        const excluded = this.excludedIds();
        return excluded.size > 0 ? kids.filter(n => !excluded.has(n.documentNodeId)) : kids;
    }

    public isExpanded(folderId: number): boolean {
        return this.expanded().has(folderId);
    }

    public isLoading(folderId: number): boolean {
        return this.loadingIds().has(folderId);
    }

    public toggleExpand(folderId: number, event: Event): void {
        event.stopPropagation();
        const isOpen = this.expanded().has(folderId);
        this.expanded.update(set => {
            const next = new Set(set);
            if (isOpen) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
        // Fetch this folder's children the first time it's opened; cached thereafter.
        if (!isOpen && !this.loadedChildren().has(folderId) && !this.loadingIds().has(folderId)) {
            void this.fetchChildren(folderId);
        }
    }

    private async fetchChildren(folderId: number): Promise<void> {
        const load = this.loadChildren();
        if (!load) return;

        this.loadingIds.update(set => new Set(set).add(folderId));
        try {
            const children = (await load(folderId))
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            this.loadedChildren.update(map => new Map(map).set(folderId, children));
        } finally {
            this.loadingIds.update(set => {
                const next = new Set(set);
                next.delete(folderId);
                return next;
            });
        }
    }

    public isDisabled(destination: FolderTreeSelection): boolean {
        if (destination === FolderTreeDestination.Root) return this.rootDisabled();
        if (typeof destination === 'number') return this.disabledIds().has(destination);
        return false;
    }

    public select(destination: FolderTreeSelection): void {
        if (this.isDisabled(destination)) return;
        this.selected.set(destination);
    }

    public isSelected(destination: FolderTreeSelection): boolean {
        return this.selected() === destination;
    }
}
